<template>
  <div class="relative h-full">
    <FileUpload
      ref="fileUploadRef"
      mode="advanced"
      :unstyled="true"
      :auto="true"
      :custom-upload="true"
      :multiple="true"
      :disabled="isUploadingDocuments || !isDraft"
      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
      :pt="fileUploadPt"
      @uploader="uploadDocuments"
    >
      <template #header="{ chooseCallback }">
        <div
          :class="[
            { hidden: !isUploadingDocuments },
            'bg-navy-900/50 absolute top-0 right-0 bottom-0 left-0 z-2 flex items-center justify-center',
          ]"
        >
          <ProgressBar class="w-md" :value="uploadProgress"></ProgressBar>
        </div>
        <!-- Expose chooseCallback to parent -->
        <slot name="upload-trigger" :choose="chooseCallback"></slot>
      </template>
      <template #content>
        <!-- Card header -->
        <div
          class="flex items-center justify-between border-b border-neutral-300 bg-neutral-200 px-6 py-4"
        >
          <h2
            class="text-navy-800 flex items-center gap-2 text-sm font-semibold"
          >
            <i class="pi pi-file text-navy-400" />
            Documents
            <span
              class="bg-navy-50 text-navy-500 inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-xs font-semibold"
            >
              {{ documents.length }}
            </span>
          </h2>
          <!-- Dropdown for imported + draft orders -->
          <template v-if="isImported && isDraft">
            <button
              v-tooltip.top="!isDraft ? 'Only available in draft' : ''"
              :disabled="!isDraft"
              class="text-navy-500 hover:bg-navy-50 hover:text-navy-700 inline-flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              @click="toggleAddMenu"
            >
              <i class="pi pi-plus text-xs" />
              Add Documents
              <i class="pi pi-chevron-down text-[10px]" />
            </button>
            <Menu ref="addMenuRef" :model="addMenuItems" :popup="true" />
          </template>
          <!-- Simple button for non-imported orders -->
          <button
            v-else
            v-tooltip.top="!isDraft ? 'Only available in draft' : ''"
            :disabled="!isDraft"
            class="text-navy-500 hover:bg-navy-50 hover:text-navy-700 inline-flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            @click="triggerUpload()"
          >
            <i class="pi pi-plus text-xs" />
            Add Documents
          </button>
        </div>

        <!-- Import progress banner -->
        <div
          v-if="order.importDocumentsStatus === 'importing'"
          class="flex items-center gap-3 border-b border-neutral-300 bg-blue-50 px-6 py-3"
        >
          <i class="pi pi-spin pi-spinner text-blue-500" />
          <div class="flex-1">
            <p class="text-sm font-medium text-blue-700">
              Importing documents...
              {{ order.importDocumentsCompleted }}/{{
                order.importDocumentsTotal
              }}
            </p>
            <div
              class="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-blue-200"
            >
              <div
                class="h-full rounded-full bg-blue-500 transition-all"
                :style="{
                  width: `${order.importDocumentsTotal ? Math.round(((order.importDocumentsCompleted ?? 0) / order.importDocumentsTotal) * 100) : 0}%`,
                }"
              />
            </div>
          </div>
        </div>

        <!-- Partial failure banner -->
        <div
          v-else-if="order.importDocumentsStatus === 'partial_failure'"
          class="flex items-center gap-3 border-b border-neutral-300 bg-amber-50 px-6 py-3"
        >
          <i class="pi pi-exclamation-triangle text-amber-500" />
          <p class="text-sm font-medium text-amber-700">
            Some documents failed to import ({{
              order.importDocumentsCompleted
            }}/{{ order.importDocumentsTotal }}
            succeeded)
          </p>
        </div>

        <DataTable
          :value="documents"
          :show-headers="false"
          size="small"
          class="flex h-full flex-1 flex-col"
          :reorderable-columns="isDraft"
          @row-reorder="reorderDocuments"
        >
          <template #empty>
            <div
              :class="[
                { invisible: isUploadingDocuments },
                'flex min-h-[200px] flex-col items-center justify-center p-12 text-center',
              ]"
            >
              <div
                class="mb-6 flex h-20 w-20 items-center justify-center rounded-full border-2 border-neutral-300"
              >
                <i class="pi pi-cloud-upload text-3xl text-neutral-400"></i>
              </div>
              <p class="text-navy-800 mb-2 text-lg font-semibold">
                Drag and drop files here to upload
              </p>
              <p class="text-navy-500 text-sm">
                Or click the Add Documents button above
              </p>
              <button
                v-if="isImported && isDraft"
                class="text-navy-500 hover:text-navy-700 mt-2 cursor-pointer text-sm font-medium transition-colors"
                @click="emit('pullDocuments')"
              >
                or pull from {{ agentName }}
              </button>
            </div>
          </template>
          <Column
            v-if="isDraft"
            row-reorder
            header-style="width: 3rem"
            body-class="pl-6! pr-0!"
          />
          <Column field="name">
            <template #body="{ data }">
              <div class="flex flex-col" :class="{ 'pl-6': !isDraft }">
                <p class="text-navy-800 text-sm font-medium">
                  {{ data.name }}
                  <i
                    v-if="data.source === 'imported'"
                    v-tooltip.top="'Imported — name is read-only'"
                    class="pi pi-lock ml-1 text-xs text-neutral-400"
                  />
                </p>
                <p class="text-xs text-neutral-400">
                  {{ data.numberOfPages }}
                  {{ data.numberOfPages === 1 ? "page" : "pages" }}
                </p>
              </div>
            </template>
          </Column>
          <Column header-style="width: 140px">
            <template #body="{ data }">
              <div class="flex items-center justify-end gap-3 py-3 pr-6">
                <StatusBadge :variant="data.finalized ? 'success' : 'neutral'">
                  {{ data.finalized ? "Finalized" : "Pending" }}
                </StatusBadge>
                <button
                  class="cursor-pointer rounded-md p-2 text-neutral-500 transition-colors hover:bg-neutral-50"
                  @click="(event) => toggleMenu(event, data)"
                >
                  <i class="pi pi-ellipsis-v text-sm"></i>
                </button>
                <Menu
                  ref="menuRef"
                  :model="getMenuItems(selectedDocument)"
                  :popup="true"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </FileUpload>

    <ReplaceDocumentModal
      v-model:visible="showReplaceModal"
      :document="selectedDocument"
      @success="handleReplaceSuccess"
    />

    <!-- Edit Document Modal -->
    <Dialog
      v-model:visible="showEditModal"
      modal
      header="Edit Document"
      :style="{ width: '32rem' }"
    >
      <form class="space-y-6" @submit.prevent="submitEditDocument">
        <AppInput
          v-model="editDocumentName"
          label="Document Name"
          placeholder="Enter document name"
          :error="editDocumentError"
          required
        />

        <div class="mt-6 flex justify-end gap-3">
          <AppButton type="button" variant="secondary" @click="closeEditModal">
            Cancel
          </AppButton>
          <AppButton type="submit" :loading="isEditingDocument">
            Update Document
          </AppButton>
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import Column from "primevue/column"
import DataTable, { type DataTableRowReorderEvent } from "primevue/datatable"
import Dialog from "primevue/dialog"
import FileUpload, { type FileUploadUploaderEvent } from "primevue/fileupload"
import Menu from "primevue/menu"
import ProgressBar from "primevue/progressbar"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import { computed, ref } from "vue"

import ReplaceDocumentModal from "../../components/ReplaceDocumentModal.vue"
import AppButton from "../../components/ui/AppButton.vue"
import AppInput from "../../components/ui/AppInput.vue"
import StatusBadge from "../../components/ui/StatusBadge.vue"
import { useDocumentMutations } from "../../composables/useDocumentMutations"
import { useDocumentUpload } from "../../composables/useDocumentUpload"
import { DocumentType, OrderType } from "../../pages/Order/types"

const toast = useToast()
const confirm = useConfirm()
const {
  updateDocument,
  destroyDocument: destroyDocumentMutation,
  reorderDocuments: reorderDocumentsMutation,
} = useDocumentMutations(toast)

const menuRef = ref()
const selectedDocument = ref<DocumentType | null>(null)
const showReplaceModal = ref(false)

// Edit modal state
const showEditModal = ref(false)
const editDocumentName = ref("")
const editDocumentError = ref<string | undefined>()
const isEditingDocument = ref(false)

const fileUploadRef = ref()

const fileUploadPt = {
  root: { class: "h-full" },
  content: ({ state }: { state: { dragover: boolean } }) => ({
    class: [
      "h-full",
      state.dragover &&
        "[&_.p-datatable]:bg-green-50 [&_.p-datatable]:border-dashed [&_.p-datatable]:border-green-200 [&_.p-datatable-header]:bg-transparent [&_tr]:bg-transparent",
    ],
  }),
  input: { class: "hidden" },
}

const { order, documents } = defineProps<{
  order: OrderType
  documents: DocumentType[]
}>()

const {
  isUploading: isUploadingDocuments,
  uploadProgress,
  uploadDocuments: uploadDocumentsBatch,
} = useDocumentUpload(toast, () => order.gid)

const isDraft = computed(() => order.status === "DRAFT")
const canReplaceDocuments = computed(() => order.status === "CLOSING")
const isImported = computed(() => !!order.importAgentRegistration)
const agentName = computed(
  () => order.importAgentRegistration?.name ?? "source system",
)

const emit = defineEmits<{
  pullDocuments: []
}>()

// Add documents dropdown menu
const addMenuRef = ref()
const addMenuItems = computed(() => [
  {
    label: "Upload Files",
    icon: "pi pi-upload",
    command: () => triggerUpload(),
  },
  {
    label: `Pull from ${agentName.value}`,
    icon: "pi pi-cloud-download",
    command: () => emit("pullDocuments"),
  },
])

const toggleAddMenu = (event: Event) => {
  addMenuRef.value.toggle(event)
}

// Expose method to trigger file upload from parent
const triggerUpload = () => {
  const input = fileUploadRef.value?.$el?.querySelector('input[type="file"]')
  input?.click()
}

defineExpose({
  triggerUpload,
})

const toggleMenu = (event: Event, doc: DocumentType) => {
  selectedDocument.value = doc
  menuRef.value.toggle(event)
}

const getMenuItems = (doc: DocumentType | null) => {
  if (!doc) return []

  const items = []

  if (doc.finalized) {
    items.push({
      label: "Signed",
      icon: "pi pi-download",
      command: () =>
        window.open(
          `/documents/${doc.gid}/download?version=finalized&disposition=attachment`,
          "_blank",
        ),
    })
  }

  items.push({
    label: "Original",
    icon: "pi pi-download",
    command: () =>
      window.open(
        `/documents/${doc.gid}/download?version=original&disposition=attachment`,
        "_blank",
      ),
  })

  if (isDraft.value && doc.source !== "imported") {
    items.push({
      label: "Edit",
      icon: "pi pi-pen-to-square",
      command: () => openEditModal(doc),
    })
  }

  if (canReplaceDocuments.value) {
    items.push({
      separator: true,
    })
    items.push({
      label: "Replace Document",
      icon: "pi pi-refresh",
      command: () => {
        showReplaceModal.value = true
      },
    })
  }

  if (isDraft.value) {
    items.push({
      separator: true,
    })
    items.push({
      label: "Delete",
      icon: "pi pi-trash",
      class: "text-red-600",
      command: () => confirmDelete(doc),
    })
  }

  return items
}

// Edit modal functions
const openEditModal = (doc: DocumentType) => {
  selectedDocument.value = doc
  editDocumentName.value = doc.name
  editDocumentError.value = undefined
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  selectedDocument.value = null
  editDocumentName.value = ""
  editDocumentError.value = undefined
}

const submitEditDocument = async () => {
  if (!selectedDocument.value) return

  if (!editDocumentName.value.trim()) {
    editDocumentError.value = "Name is required"
    return
  }

  isEditingDocument.value = true
  editDocumentError.value = undefined

  try {
    const result = await updateDocument(
      selectedDocument.value.id,
      editDocumentName.value,
    )

    if (!result.success) {
      if (result.errors) {
        editDocumentError.value = result.errors.join(", ")
      }
      return
    }

    closeEditModal()
    router.reload()
  } finally {
    isEditingDocument.value = false
  }
}

const confirmDelete = (doc: DocumentType) => {
  confirm.require({
    message: `Are you sure you want to delete ${doc.name}?`,
    header: "Confirm Delete",
    icon: "pi pi-exclamation-triangle",
    acceptClass: "p-button-danger",
    accept: () => {
      void deleteDocument(doc)
    },
  })
}

const deleteDocument = async (doc: DocumentType) => {
  const result = await destroyDocumentMutation(doc.id)
  if (result.success) {
    router.reload()
  }
}

const handleReplaceSuccess = () => {
  toast.add({
    severity: "success",
    summary: "Success",
    detail: "Document replaced successfully",
  })
  router.reload()
}

const uploadDocuments = (event: FileUploadUploaderEvent) => {
  const files = event.files as File[]
  uploadDocumentsBatch(files, () => fileUploadRef.value?.clear())
}

const reorderDocuments = async (event: DataTableRowReorderEvent) => {
  const documentIds = event.value.map((document) => document.id)
  await reorderDocumentsMutation(order.gid, documentIds)
  router.reload()
}
</script>
